/**
 * Lazy-load YouTube/Vimeo iframes and deferred native video sources when in viewport.
 */
(function () {
  const DEFER_IFRAME_ATTR = "data-video-defer-src";
  const LEGACY_DEFER_IFRAME_ATTR = "data-aw-defer-src";
  const DEFER_NATIVE_ATTR = "data-video-defer-native";

  let observer = null;
  const observed = new WeakSet();

  function loadIframe(iframe) {
    const deferredSrc =
      iframe.getAttribute(DEFER_IFRAME_ATTR) ||
      iframe.getAttribute(LEGACY_DEFER_IFRAME_ATTR);
    if (!deferredSrc || iframe.getAttribute("src")) return;
    iframe.setAttribute("src", deferredSrc);
    iframe.removeAttribute(DEFER_IFRAME_ATTR);
    iframe.removeAttribute(LEGACY_DEFER_IFRAME_ATTR);
    iframe.closest(".video-embed")?.classList.add("video-embed--loaded");
  }

  function loadNativeVideo(video) {
    const source = video.querySelector(`source[${DEFER_IFRAME_ATTR}]`);
    if (source && !source.getAttribute("src")) {
      source.setAttribute("src", source.getAttribute(DEFER_IFRAME_ATTR));
      source.removeAttribute(DEFER_IFRAME_ATTR);
    }
    video.load();
    if (video.autoplay) {
      video.play().catch(() => {});
    }
    video.removeAttribute(DEFER_NATIVE_ATTR);
  }

  function activateTarget(target) {
    if (target.matches(`iframe[${DEFER_IFRAME_ATTR}], iframe[${LEGACY_DEFER_IFRAME_ATTR}]`)) {
      loadIframe(target);
      return;
    }
    if (target.matches(`video[${DEFER_NATIVE_ATTR}]`)) {
      loadNativeVideo(target);
    }
  }

  function ensureObserver() {
    if (observer) return observer;
    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          activateTarget(entry.target);
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: "200px 0px", threshold: 0.1 },
    );
    return observer;
  }

  function observeTarget(target) {
    if (observed.has(target)) return;
    observed.add(target);
    ensureObserver().observe(target);
  }

  function initVideoEmbedDefer(root = document) {
    root
      .querySelectorAll(
        `iframe[${DEFER_IFRAME_ATTR}]:not([src]), iframe[${LEGACY_DEFER_IFRAME_ATTR}]:not([src]), video[${DEFER_NATIVE_ATTR}]`,
      )
      .forEach(observeTarget);
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", () => initVideoEmbedDefer());
  } else {
    initVideoEmbedDefer();
  }

  document.addEventListener("shopify:section:load", (event) => {
    initVideoEmbedDefer(event.target);
  });
})();
